import React, { useState, useEffect, useCallback } from 'react';
import AntTable from 'common_components/AntTable';
import EditButtonRenderer from 'common_components/EditButtonRenderer';
import {
  dateTimeTimestamptzFormatter,
  userGroupFormatter,
} from 'common_components/AntTable/tableUtils';
import TableToolbar from 'common_components/AntTable/Toolbar';
import { push } from 'connected-react-router';
import permission from 'permissions';
import { useSelector, useDispatch } from 'react-redux';
import { getCurrentUser } from 'reducers/user';
import {
  useDeleteUserMutation,
  useDestroyUserMutation,
} from 'reducers/user/api';
import { useErrorHandler } from 'common_components/ErrorContext';
import { Sorter } from 'utils/sorter';
import styled from 'styled-components';
import { selectTableStateById } from 'reducers/table';
import { message } from 'antd';

const UserTableComponent = (props) => {
  const { rowData, isLoading } = props;
  const [dataSource, setDataSource] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const tableIdSelector = useSelector((state) =>
    selectTableStateById(state, 'user')
  );
  const [columns, setColumns] = useState(() => {
    let cols = [
      {
        title: '',
        dataIndex: 'edit',
        align: 'center',
        render: (text, record) => {
          return (
            <EditButtonRenderer data={record} onClick={handleEditClicked} />
          );
        },
        width: 50,
      },
      {
        title: 'Company',
        dataIndex: 'company',
        render: (text, record) => {
          return text && text.company_bzp;
        },
        width: 200,
        sorter: {
          compare: Sorter.DEFAULT,
          field: 'company_bzp',
        },
        movable: true,
        searchable: true,
      },
      {
        title: 'Username',
        dataIndex: 'username',
        width: 150,
        sorter: {
          compare: Sorter.DEFAULT,
        },
        movable: true,
        searchable: true,
      },
      {
        title: 'Fullname (EN)',
        dataIndex: 'fullname_en',
        width: 150,
        sorter: {
          compare: Sorter.DEFAULT,
        },
        movable: true,
        searchable: true,
      },
      {
        title: 'Fullname (TH)',
        dataIndex: 'fullname_th',
        width: 150,
        sorter: {
          compare: Sorter.DEFAULT,
        },
        movable: true,
        searchable: true,
      },
      {
        title: 'Email',
        dataIndex: 'email',
        width: 150,
        sorter: {
          compare: Sorter.DEFAULT,
        },
        movable: true,
        searchable: true,
      },
      {
        title: 'Group',
        dataIndex: 'groups',
        render: (text, record) => {
          return userGroupFormatter({ value: text });
        },
        sorter: {
          compare: Sorter.DEFAULT,
          array: true,
          field: 'name',
        },
        movable: true,
      },
      {
        title: 'Yard',
        dataIndex: 'yard',
        render: (text, record) => {
          return text?.short_name;
        },
        sorter: {
          compare: Sorter.DEFAULT,
          field: 'short_name',
        },
        movable: true,
      },
      {
        title: 'Updated time',
        dataIndex: 'updated_at',
        render: (text, record) => {
          return dateTimeTimestamptzFormatter({ value: text });
        },
        sorter: {
          compare: Sorter.DATE,
        },
        movable: true,
      },
      {
        title: 'Created time',
        dataIndex: 'created_at',
        render: (text, record) => {
          return dateTimeTimestamptzFormatter({ value: text });
        },
        sorter: {
          compare: Sorter.DATE,
        },
        movable: true,
      },
    ];
    if (user && user.is_superadmin) {
      cols.splice(1, 0, {
        title: 'Is Active',
        dataIndex: 'is_active',
        width: 100,
        render: (text, record) => {
          return text.toString();
        },
        sorter: {
          compare: Sorter.DEFAULT,
        },
        movable: true,
      });
      cols.splice(1, 0, {
        title: 'SuperAdmin',
        dataIndex: 'is_superadmin',
        render: (text, record) => {
          return text.toString();
        },
        sorter: {
          compare: Sorter.DEFAULT,
        },
        movable: true,
      });
    }
    // Add Show/Hide status from Redux store
    if (tableIdSelector) {
      cols = cols.map((c) => ({
        ...c,
        ...tableIdSelector.find((t) => t.dataIndex === c.dataIndex),
      }));
    }

    return cols;
  });

  const [showDelete, setShowDelete] = useState(false);

  const dispatch = useDispatch();
  const { setErrorData } = useErrorHandler();
  const user = useSelector(getCurrentUser);

  useEffect(() => {
    setDataSource(rowData);
  }, [rowData]);

  const [
    deleteUser,
    {
      isSuccess: isSuccessDeleting,
      isUninitialized: isUninitDeleting,
      error: errorDeleting,
      isLoading: isDeleting,
    },
  ] = useDeleteUserMutation();
  const [
    destroyUser,
    {
      isSuccess: isSuccessDestroying,
      isUninitialized: isUninitDestroying,
      error: errorDestroying,
      isLoading: isDestroying,
    },
  ] = useDestroyUserMutation();

  useEffect(() => {
    if (errorDeleting) {
      setErrorData(errorDeleting);
    }
    if (errorDestroying) {
      setErrorData(errorDestroying);
    }
  }, [errorDeleting, errorDestroying, setErrorData]);

  useEffect(() => {
    if (!isUninitDeleting && isSuccessDeleting) {
      message.success('Data was successfully deleted');
    }
  }, [isSuccessDeleting, isUninitDeleting]);

  useEffect(() => {
    if (!isUninitDestroying && isSuccessDestroying) {
      message.success('Data was successfully deleted');
    }
  }, [isSuccessDestroying, isUninitDestroying]);

  const onSearch = useCallback(
    (searchTerm) => {
      if (rowData && rowData.length > 0) {
        let searchCols = columns.filter((c) => c.searchable);
        if (searchCols.length > 0 && searchTerm) {
          let filterData = rowData.filter((d) =>
            searchCols.some(
              (s) =>
                d[s.dataIndex] &&
                d[s.dataIndex]
                  .toString()
                  .toLowerCase()
                  .includes(searchTerm.toLowerCase())
            )
          );
          setDataSource(filterData);
        } else {
          setDataSource(rowData);
        }
      }
      clearSelectedRows();
    },
    [columns, rowData]
  );

  const onCreate = () => {
    dispatch(push(`/app/user_mng/users/create`));
  };

  const handleEditClicked = (cell, readOnly) => {
    dispatch(push(`/app/user_mng/users/${cell.id}`));
  };

  const onDelete = (isPermanentDelete) => {
    // TODO: Bulk delete
    for (let row of selectedRows) {
      if (isPermanentDelete) {
        destroyUser({ id: row.id });
      } else {
        deleteUser({ id: row.id, is_active: false });
      }
    }

    clearSelectedRows();
  };

  const onReload = () => {
    props.refetchData();
    clearSelectedRows();
  };

  const clearSelectedRows = () => {
    setSelectedRows([]);
    setSelectedRowKeys([]);
    setShowDelete(false);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedRowKeys, selectedRows) => {
      setSelectedRowKeys(selectedRowKeys);
      setSelectedRows(selectedRows);

      if (permission.checkPermission(user, 'user', ['d'])) {
        setShowDelete(selectedRows.length > 0 ? true : false);
      }
    },
  };

  return (
    <Container>
      <TableToolbar
        onCreate={onCreate}
        onDelete={onDelete}
        onReload={onReload}
        showDelete={showDelete}
        showReload={true}
        isLoading={isLoading}
        isDeleting={isDeleting || isDestroying}
        showPermanentDelete={permission.isSuperAdmin(user)}
        showAdd={permission.checkPermission(user, 'user', ['c'])}
        showSearch={true}
        columns={columns}
        setColumns={setColumns}
        onSearch={onSearch}
      />
      <AntTable
        rowKey='id'
        tableId={'user'}
        bordered
        rowSelection={rowSelection}
        showSorterTooltip={false}
        loading={isLoading || isDeleting || isDestroying}
        columns={columns}
        dataSource={dataSource}
        scroll={{ x: 'max-content' }}
        // Below is for right click context menu
        showContextMenu={true}
        selectedRowKeys={selectedRowKeys}
        setSelectedRowKeys={setSelectedRowKeys}
        menuActions={[
          { label: 'Edit', action: (row) => handleEditClicked(row) },
          {
            label: 'Delete',
            action: (row, isPermanentDelete) => {
              if (isPermanentDelete) {
                destroyUser({ id: row.id });
              } else {
                deleteUser({ id: row.id, is_active: false });
              }
            },
            delete: true,
          },
        ]}
        showPermanentDelete={permission.isSuperAdmin(user)}
      />
    </Container>
  );
};

const Container = styled.div`
  padding: 0 10px;
`;

export default UserTableComponent;
